/**
 * Interiors (ภายในอาคาร): going in through a building's door, up and down its stairs, and out again.
 *
 * Each floor of a building is a small map of its own, generated from its plan in data/interiors.ts.
 * Going in, the World swaps its map for the floor's and sets the zone's own people aside (the
 * player, the pet and the party come along); coming out puts them all back and the player at the
 * door outside. On a server each interior floor is an instance like a zone, made from the same data
 * by the same `generateInterior`, so this never lives on the client. Design: docs/INTERIORS.md.
 */
import {
  DEFAULT_RULES,
  DOOR_LINKS,
  INTERIORS,
  LIGHT_CELLS,
  SOLID_CELLS,
  STAIR_CELLS,
  STAIR_DIR,
  STAIR_PAIR,
  WALL_CELLS,
  type DoorLink,
  type FloorDef,
  type IndoorRules,
  type InteriorDef,
} from './data/interiors';
import { FOLKS } from './data/folk';
import { combatBlockText } from './data/combat';
import { ZONES } from './data/zones';
import { PK } from './karma';
import { Tile, tileAt, type Facing, type GameMap, type Point } from './map';
import { freeAt, nearestFloor } from './path';
import type { Entity, PlayerCommand, World, WorldEvent } from './world';

/** Seconds the screen takes to fade out before the scene changes (it fades back in after). */
export const DOOR_FADE = 0.3;
/** How near a door or stairs the player must be to use it with E (tiles). */
export const DOOR_REACH = 1.6;
/** Tiles of empty ground round a floor's plan on its map (the map's edge is rock). */
const MARGIN = 2;

export type InteriorCommand =
  /** Go in by the door of the building near the player (or the one named, if near). */
  | { t: 'enter'; building?: string }
  /** Go out by the door near the player. */
  | { t: 'exit' }
  /** Take the stairs near the player: up (1), down (−1), or whichever is nearest. */
  | { t: 'changeFloor'; dir?: 1 | -1 };

export type InteriorEvent =
  /** A door or stairs was taken: the screen fades out, and the scene changes `seconds` later. */
  | { type: 'doorFade'; to: 'enter' | 'exit' | 'floor'; seconds: number }
  | { type: 'enterInterior'; interior: string; floor: number; building: string }
  | { type: 'changeFloor'; interior: string; from: number; to: number }
  | { type: 'exitInterior'; interior: string; building: string };

/** Where a saved character is indoors (progress.indoors): the building, floor and spot. */
export interface IndoorsState {
  interior: string;
  floor: number;
  /** The building outside whose door leads back out. */
  building: string;
  x: number;
  y: number;
}

const STEP: Readonly<Record<Facing, readonly [number, number]>> = {
  n: [0, -1],
  e: [1, 0],
  s: [0, 1],
  w: [-1, 0],
};

const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

// ───────────────────────── floors as maps ─────────────────────────

/** A plan turned so its bottom (south) side faces `facing`. */
function turnPlan(plan: readonly string[], facing: Facing): string[] {
  const H = plan.length;
  const W = Math.max(...plan.map((r) => r.length));
  const at = (r: number, c: number) => plan[r]?.[c] ?? ' ';
  const build = (h: number, w: number, f: (r: number, c: number) => string) =>
    Array.from({ length: h }, (_, r) => Array.from({ length: w }, (_, c) => f(r, c)).join(''));
  switch (facing) {
    case 's':
      return build(H, W, at);
    case 'n':
      return build(H, W, (r, c) => at(H - 1 - r, W - 1 - c));
    case 'e':
      return build(W, H, (r, c) => at(c, W - 1 - r));
    case 'w':
      return build(W, H, (r, c) => at(H - 1 - c, r));
  }
}

/** A floor's plan as built: swapped cells, mirrored, turned to face the building's door. */
export function floorPlan(def: InteriorDef, fl: FloorDef): string[] {
  let rows = fl.plan.map((r) => [...r].map((ch) => def.swap?.[ch] ?? ch).join(''));
  if (def.mirror) rows = rows.map((r) => [...r].reverse().join(''));
  return turnPlan(rows, def.facing ?? 's');
}

export function getFloor(id: string, level: number): FloorDef | undefined {
  return INTERIORS[id]?.floors.find((f) => f.level === level);
}

/** A cell is walked on: floor, mats, stools, balcony, stairs and the spots marked in the plan. */
export const walkableCell = (ch: string): boolean =>
  ch !== ' ' && ch !== '~' && !WALL_CELLS.includes(ch) && !SOLID_CELLS.includes(ch);

const built = new Map<string, GameMap>();

/** The map of one floor of an interior (the same every time: it is made from data alone). */
export function generateInterior(id: string, level: number): GameMap {
  const key = `${id}@${level}`;
  const hit = built.get(key);
  if (hit) return hit;
  const def = INTERIORS[id];
  const fl = getFloor(id, level);
  if (!def || !fl) throw new Error(`No floor ${level} in interior ${id}`);
  const rows = floorPlan(def, fl);
  const ph = rows.length;
  const pw = Math.max(...rows.map((r) => r.length));
  // Square, for the camera's quarter turns, with the plan in the middle.
  const n = Math.max(pw, ph) + MARGIN * 2;
  const oi = Math.floor((n - pw) / 2);
  const oj = Math.floor((n - ph) / 2);
  const plan = Array.from({ length: n }, (_, j) =>
    Array.from({ length: n }, (_, i) => rows[j - oj]?.[i - oi] ?? ' ').join(''),
  );
  const tiles = new Uint8Array(n * n);
  const variant = new Uint8Array(n * n);
  for (let j = 0; j < n; j++)
    for (let i = 0; i < n; i++) {
      const ch = plan[j]![i]!;
      const k = j * n + i;
      if (ch === ' ') {
        tiles[k] = Tile.Rock;
        variant[k] = 4;
      } else if (ch === '~') tiles[k] = Tile.Water;
      else if (WALL_CELLS.includes(ch) || SOLID_CELLS.includes(ch)) tiles[k] = Tile.Building;
      else tiles[k] = Tile.Paved;
    }
  const centre = { x: n / 2, y: n / 2 };
  const far = { x: -64, y: -64 };
  const m: GameMap = {
    w: n,
    h: n,
    seed: def.seed,
    tiles,
    variant,
    plaza: centre,
    lair: far,
    spawn: centre,
    elder: centre,
    jail: far,
    arena: centre,
    portals: [],
    duel: far,
    regions: {
      wolves: [0, 0, 0, 0],
      bandits: [0, 0, 0, 0],
      west: [0, 0, 0, 0],
      east: [0, 0, 0, 0],
      graves: [0, 0, 0, 0],
    },
    structures: [],
    biome: 'plains',
    zone: def.zone,
    kind: ZONES[def.zone].kind,
    owner: new Uint16Array(n * n),
    interior: { id, floor: level, plan },
  };
  // Where someone arriving with no particular door or stairs stands.
  const f = roomFeatures(m);
  const spawn = f.doors[0]?.landing ?? f.stairs[0]?.landing ?? nearOpen(m, centre);
  const out: GameMap = { ...m, spawn, elder: spawn, arena: spawn };
  built.set(key, out);
  features.set(out, f);
  return out;
}

/** A door out of a floor: the door tile, the floor tile inside it, and the way to it from there. */
export interface InnerDoor {
  readonly index: number;
  readonly i: number;
  readonly j: number;
  readonly landing: Point;
  readonly toward: readonly [number, number];
}

/** A flight of stairs (a ladder, a trapdoor): its tiles, where one steps off it, and where it goes. */
export interface Flight {
  readonly cell: string;
  readonly dir: 1 | -1;
  /** k-th flight of its kind on the floor, which arrives at the k-th of the paired kind. */
  readonly index: number;
  readonly tiles: readonly Point[];
  readonly landing: Point;
}

export interface RoomFeatures {
  readonly doors: readonly InnerDoor[];
  readonly stairs: readonly Flight[];
  /** NPC spots by their digit. */
  readonly spots: Readonly<Record<string, Point>>;
  readonly monsters: readonly Point[];
  readonly residents: readonly Point[];
  /** Lanterns, the hearth, altar candles. */
  readonly lights: readonly (Point & { readonly cell: string })[];
  /** Windows (daylight in). */
  readonly windows: readonly Point[];
}

const features = new WeakMap<GameMap, RoomFeatures>();
const NEIGHBOURS = [
  [0, 1],
  [1, 0],
  [0, -1],
  [-1, 0],
] as const;

function nearOpen(m: GameMap, p: Point): Point {
  if (freeAt(m, p.x, p.y)) return p;
  const f = nearestFloor(m, p.x, p.y, 6);
  return f ? { x: f[0], y: f[1] } : p;
}

/** The doors, stairs, spots and lights of an interior floor's map (empty for a zone's map). */
export function roomFeatures(m: GameMap): RoomFeatures {
  const hit = features.get(m);
  if (hit) return hit;
  const plan = m.interior?.plan ?? [];
  const cell = (i: number, j: number) => plan[j]?.[i] ?? ' ';
  const centre = (i: number, j: number): Point => ({ x: i + 0.5, y: j + 0.5 });
  const landingOf = (i: number, j: number): [number, number] | null => {
    for (const [di, dj] of NEIGHBOURS) {
      const ch = cell(i + di, j + dj);
      if (walkableCell(ch) && !STAIR_CELLS.includes(ch)) return [i + di, j + dj];
    }
    return null;
  };
  const doors: InnerDoor[] = [];
  const stairs: Flight[] = [];
  const spots: Record<string, Point> = {};
  const monsters: Point[] = [];
  const residents: Point[] = [];
  const lights: (Point & { cell: string })[] = [];
  const windows: Point[] = [];
  const seen = new Set<number>();
  const count: Record<string, number> = {};
  for (let j = 0; j < plan.length; j++)
    for (let i = 0; i < plan[j]!.length; i++) {
      const ch = cell(i, j);
      if (ch === 'D') {
        const l = landingOf(i, j);
        if (l)
          doors.push({
            index: doors.length,
            i,
            j,
            landing: centre(l[0], l[1]),
            toward: [i - l[0], j - l[1]],
          });
      } else if (STAIR_CELLS.includes(ch) && !seen.has(j * m.w + i)) {
        // The whole flight: the tiles of the same kind joined to this one.
        const tiles: Point[] = [];
        const stack: [number, number][] = [[i, j]];
        seen.add(j * m.w + i);
        let landing: [number, number] | null = null;
        while (stack.length) {
          const [a, b] = stack.pop()!;
          tiles.push(centre(a, b));
          landing ??= landingOf(a, b);
          for (const [di, dj] of NEIGHBOURS) {
            const [x, y] = [a + di, b + dj];
            if (cell(x, y) === ch && !seen.has(y * m.w + x)) {
              seen.add(y * m.w + x);
              stack.push([x, y]);
            }
          }
        }
        tiles.sort((p, q) => p.y - q.y || p.x - q.x);
        const index = (count[ch] = (count[ch] ?? -1) + 1);
        stairs.push({
          cell: ch,
          dir: STAIR_DIR[ch]!,
          index,
          tiles,
          landing: landing ? centre(landing[0], landing[1]) : tiles[0]!,
        });
      } else if (ch >= '1' && ch <= '9') spots[ch] = centre(i, j);
      else if (ch === 'm') monsters.push(centre(i, j));
      else if (ch === 'r') residents.push(centre(i, j));
      else if (ch === 'w') windows.push(centre(i, j));
      if (LIGHT_CELLS.includes(ch)) lights.push({ ...centre(i, j), cell: ch });
    }
  const f: RoomFeatures = { doors, stairs, spots, monsters, residents, lights, windows };
  features.set(m, f);
  return f;
}

/** The flight on the floor above or below that a flight arrives at, if there is one. */
export function flightTarget(
  id: string,
  level: number,
  fl: Flight,
): { level: number; flight: Flight } | null {
  const to = level + fl.dir;
  if (!getFloor(id, to)) return null;
  const pair = STAIR_PAIR[fl.cell];
  const there = roomFeatures(generateInterior(id, to)).stairs.filter((s) => s.cell === pair);
  const flight = there[fl.index];
  return flight ? { level: to, flight } : null;
}

// ───────────────────────── doors outside ─────────────────────────

/** A door of a building outside that opens onto an interior. */
export interface ExteriorDoor {
  readonly building: string;
  readonly link: DoorLink;
  /** The door's tile (in the building's wall). */
  readonly i: number;
  readonly j: number;
  readonly facing: Facing;
  /** Where someone stands before the door (and comes out to). */
  readonly out: Point;
}

const doorCache = new WeakMap<GameMap, ExteriorDoor[]>();

/** The doors on a zone's map that lead inside: buildings with an id and a door that a link names. */
export function exteriorDoors(m: GameMap): readonly ExteriorDoor[] {
  const hit = doorCache.get(m);
  if (hit) return hit;
  const out: ExteriorDoor[] = [];
  if (!m.interior) {
    const linkOf = (building: string) => DOOR_LINKS.find((l) => l.zone === m.zone && l.building === building);
    for (const st of m.structures) {
      const link = st.id && st.door ? linkOf(st.id) : undefined;
      if (!link || !st.door) continue;
      const [fx, fy] = STEP[st.door.facing];
      out.push({
        building: link.building,
        link,
        i: st.door.i,
        j: st.door.j,
        facing: st.door.facing,
        out: { x: st.door.i + fx + 0.5, y: st.door.j + fy + 0.5 },
      });
    }
  }
  doorCache.set(m, out);
  return out;
}

// ───────────────────────── saves ─────────────────────────

/** Repairs the saved place indoors against the data (null when it no longer makes sense). */
export function parseIndoors(raw: unknown, zone: string): IndoorsState | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<IndoorsState>;
  const def = typeof r.interior === 'string' ? INTERIORS[r.interior] : undefined;
  if (!def || typeof r.building !== 'string') return null;
  if (!DOOR_LINKS.some((l) => l.interior === def.id && l.building === r.building && l.zone === zone))
    return null;
  const floor = typeof r.floor === 'number' && getFloor(def.id, r.floor) ? r.floor : 0;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : -1);
  return { interior: def.id, floor, building: r.building, x: num(r.x), y: num(r.y) };
}

// ───────────────────────── the World's side ─────────────────────────

/** What the interiors need of the World (its private parts, lent through closures). */
export interface InteriorHost {
  readonly world: World;
  add(o: Partial<Entity> & { kind: Entity['kind']; x: number; y: number }): Entity;
  spawnMonster(id: string, x: number, y: number): Entity;
  emit(e: WorldEvent): void;
  notice(text: string): void;
  dismount(): void;
  /** Lets go of held input: the run, the chase, a walk to someone. */
  halt(): void;
  face(e: Entity, at: Point): void;
}

/** Commands refused indoors outright: they need the open world. */
const OUTDOOR_ONLY = new Set<PlayerCommand['t']>([
  'gather',
  'plant',
  'danceStart',
  'danceJoin',
  'ladderJoin',
  'arenaFight',
]);
const COMBAT = new Set<PlayerCommand['t']>(['attack', 'engage', 'skill', 'targetNearest']);

/** Someone spoken to indoors: their lines (said in turn) and whether they keep a shop. */
interface Person {
  readonly lines: readonly string[];
  readonly shop: boolean;
  said: number;
}

export class Interiors {
  /** Where the player is: null outside. */
  where: { interior: string; floor: number; building: string } | null = null;
  /** The zone's map and its people, set aside while the player is inside. */
  private outside: { map: GameMap; stash: Entity[] } | null = null;
  /** A door or stairs taken, waiting for the fade. */
  private pending: { t: number; go: () => void } | null = null;
  /** Seconds before a door or stairs can be taken again (no bouncing straight back). */
  private cool = 0;
  private noticeT = 0;
  private readonly people = new Map<number, Person>();

  constructor(private readonly h: InteriorHost) {}

  get inside(): boolean {
    return !!this.where;
  }

  /** A door or stairs is being taken (the screen is fading). */
  get busy(): boolean {
    return !!this.pending;
  }

  def(): InteriorDef | null {
    return this.where ? (INTERIORS[this.where.interior] ?? null) : null;
  }

  floor(): FloorDef | null {
    return this.where ? (getFloor(this.where.interior, this.where.floor) ?? null) : null;
  }

  /** The rules indoors (null outside, where everything is allowed as usual). */
  rules(): IndoorRules | null {
    const d = this.def();
    if (!d) return null;
    return { ...DEFAULT_RULES, ...d.rules, ...this.floor()?.rules };
  }

  /** The doors outside that lead in (none while inside). */
  doors(): readonly ExteriorDoor[] {
    return this.where ? [] : exteriorDoors(this.h.world.map);
  }

  private carried(e: Entity): boolean {
    const w = this.h.world;
    return e === w.player || e === w.pet || w.inParty(e);
  }

  /** What E does here: go in, go out or take the stairs (null with nothing in reach). */
  nearestUse(): InteriorCommand | null {
    const p = this.h.world.player;
    if (this.pending || p.dead) return null;
    if (!this.where) {
      let best: ExteriorDoor | null = null;
      for (const d of this.doors())
        if (dist(p, d.out) < DOOR_REACH && (!best || dist(p, d.out) < dist(p, best.out))) best = d;
      return best ? { t: 'enter', building: best.building } : null;
    }
    const f = roomFeatures(this.h.world.map);
    let best: { d: number; cmd: InteriorCommand } | null = null;
    for (const d of f.doors) {
      const r = dist(p, { x: d.i + 0.5, y: d.j + 0.5 });
      if (r < DOOR_REACH + 0.4 && (!best || r < best.d)) best = { d: r, cmd: { t: 'exit' } };
    }
    for (const s of f.stairs)
      for (const c of s.tiles) {
        const r = dist(p, c);
        if (r < DOOR_REACH && (!best || r < best.d)) best = { d: r, cmd: { t: 'changeFloor', dir: s.dir } };
      }
    return best?.cmd ?? null;
  }

  /** Whether a command is refused by the rules indoors (and says why, now and then). */
  refuse(cmd: PlayerCommand): boolean {
    const r = this.rules();
    if (!r) return false;
    const w = this.h.world;
    let why: string | null | 'quiet' = null;
    if (OUTDOOR_ONLY.has(cmd.t)) why = 'ออกไปข้างนอกก่อน';
    else if (COMBAT.has(cmd.t)) why = r.combat ? null : 'ที่นี่ห้ามต่อสู้';
    else
      switch (cmd.t) {
        case 'sprint':
          why = cmd.on && !r.sprint ? 'quiet' : null;
          break;
        case 'jump':
        case 'dash':
          why = r.jump ? null : 'ในอาคารใช้วิชาตัวเบาไม่ได้';
          break;
        case 'mount':
          why = r.mount || w.player.mount ? null : 'ในอาคารขี่พาหนะไม่ได้';
          break;
        case 'fly':
          why = r.mount ? null : 'quiet';
          break;
        case 'auto':
          why = cmd.on && !r.combat ? 'ที่นี่ห้ามต่อสู้' : null;
          break;
        case 'pk':
          why = cmd.on && r.safe ? 'ในอาคารเปิด PK ไม่ได้' : null;
          break;
        case 'gm':
          why = ['teleport', 'crowd', 'spawnBoss'].includes(cmd.cmd.op) ? 'ออกไปข้างนอกก่อน' : null;
          break;
      }
    if (!why) return false;
    if (why !== 'quiet' && this.noticeT <= 0) {
      this.noticeT = 1.5;
      this.h.notice(why);
    }
    return true;
  }

  apply(cmd: InteriorCommand): void {
    const w = this.h.world;
    const p = w.player;
    if (this.pending || p.dead || p.jailT > 0) return;
    switch (cmd.t) {
      case 'enter': {
        if (this.where) return;
        const near = this.doors()
          .filter((d) => (!cmd.building || d.building === cmd.building) && dist(p, d.out) < DOOR_REACH + 0.4)
          .sort((a, b) => dist(p, a.out) - dist(p, b.out));
        const d = near[0];
        if (!d) return;
        if (p.combatT > 0) return this.h.notice(combatBlockText(p, 'เข้าอาคารไม่ได้'));
        if (w.duel) return this.h.notice('อยู่ในคิวประลอง เข้าอาคารไม่ได้');
        if (p.pkPoints > PK.red) return this.h.notice('ชาวบ้านปิดประตูใส่คนชื่อแดง');
        this.fade('enter', () => this.goIn(d));
        return;
      }
      case 'exit': {
        if (!this.where) return;
        const f = roomFeatures(w.map);
        if (!f.doors.some((d) => dist(p, { x: d.i + 0.5, y: d.j + 0.5 }) < DOOR_REACH + 0.4)) return;
        this.fade('exit', () => this.leave());
        return;
      }
      case 'changeFloor': {
        if (!this.where) return;
        let best: { d: number; s: Flight } | null = null;
        for (const s of roomFeatures(w.map).stairs) {
          if (cmd.dir && s.dir !== cmd.dir) continue;
          for (const c of s.tiles) {
            const r = dist(p, c);
            if (r < DOOR_REACH + 0.4 && (!best || r < best.d)) best = { d: r, s };
          }
        }
        if (best) this.take(best.s);
        return;
      }
    }
  }

  /** Doors walked into and stairs stepped onto; the fade; keeping the save's place up to date. */
  step(dt: number): void {
    this.cool = Math.max(0, this.cool - dt);
    this.noticeT = Math.max(0, this.noticeT - dt);
    if (this.pending) {
      if ((this.pending.t -= dt) <= 0) {
        const go = this.pending.go;
        this.pending = null;
        go();
      }
      return;
    }
    const w = this.h.world;
    const p = w.player;
    if (this.where) {
      const s = w.progress.indoors;
      if (s) {
        s.x = p.x;
        s.y = p.y;
      }
      // Jailed (a red name struck down indoors): out, and off to the jail.
      if (p.jailT > 0) {
        this.leave();
        Object.assign(p, { x: w.map.jail.x, y: w.map.jail.y, path: null });
        return;
      }
      // Somehow off the floor (a GM jump): back to where the floor is entered.
      if (tileAt(w.map, Math.floor(p.x), Math.floor(p.y)) === Tile.Rock) {
        p.x = w.map.spawn.x;
        p.y = w.map.spawn.y;
      }
    }
    // (Running into a wall with วิ่งพันลี้ runs up it instead: only walking goes in.)
    if (p.dead || p.z > 0.5 || this.cool > 0 || !p.moving || p.sprinting || p.climbing) return;
    // Walking into a door: standing before it and pressing on toward it.
    const pressing = (at: Point, dx: number, dy: number, r: number) =>
      dist(p, at) < r && p.dirX * dx + p.dirY * dy > 0.55;
    if (!this.where) {
      if (p.combatT > 0 || w.duel) return;
      for (const d of this.doors()) {
        const [fx, fy] = STEP[d.facing];
        if (pressing(d.out, -fx, -fy, 0.75)) return this.apply({ t: 'enter', building: d.building });
      }
      return;
    }
    const f = roomFeatures(w.map);
    for (const d of f.doors)
      if (pressing(d.landing, d.toward[0], d.toward[1], 0.75)) return this.apply({ t: 'exit' });
    const [i, j] = [Math.floor(p.x), Math.floor(p.y)];
    const s = f.stairs.find((s) => s.tiles.some((c) => Math.floor(c.x) === i && Math.floor(c.y) === j));
    if (s) this.take(s);
  }

  /** Someone indoors spoken to: their next line (and the shop, for a shopkeeper). */
  talk(npc: Entity): boolean {
    const who = this.people.get(npc.id);
    if (!who) return false;
    const text = who.lines[who.said++ % who.lines.length]!.replaceAll('{name}', this.h.world.progress.name);
    this.h.emit({ type: 'dialog', npcId: npc.id, text, action: who.shop ? 'shop' : null });
    return true;
  }

  /** Near a shopkeeper indoors (they sell the merchant's goods). */
  nearShop(): boolean {
    const w = this.h.world;
    for (const [id, who] of this.people) {
      const e = w.entities.get(id);
      if (who.shop && e && dist(w.player, e) <= 5) return true;
    }
    return false;
  }

  /** A character saved indoors goes back in (when the world is made, after the zone is set up). */
  restore(): void {
    const w = this.h.world;
    const s = w.progress.indoors;
    if (!s) return;
    const door = exteriorDoors(w.map).find(
      (d) => d.building === s.building && d.link.interior === s.interior,
    );
    if (!door || !getFloor(s.interior, s.floor)) {
      w.progress.indoors = null;
      return;
    }
    const at = { x: s.x, y: s.y };
    this.setAside(door);
    this.setFloor(s.interior, s.floor, door.building, (m) => (freeAt(m, at.x, at.y) ? at : m.spawn));
    this.h.emit({ type: 'enterInterior', interior: s.interior, floor: s.floor, building: door.building });
  }

  /**
   * A net replica follows where the server has put the player (docs/ONLINE.md): in a building, on this floor, or
   * outside. The map is swapped at once, as `goIn`, `take` and `leave` do after their fade (no fade, no command).
   * The room's people and monsters are not made here: the server's come in its snapshots, under its ids
   * (net/snapshot.ts `syncStandIns`), so talking to them reaches the server's.
   */
  follow(to: { interior: string; floor: number; building: string; x: number; y: number } | null): void {
    const w = this.h.world;
    this.pending = null;
    if (!to) {
      if (this.where) this.leave();
      return;
    }
    if (!getFloor(to.interior, to.floor)) return;
    const at = { x: to.x, y: to.y };
    const was = this.where;
    if (was && was.interior === to.interior && was.floor === to.floor && was.building === to.building) return;
    if (!was) {
      const door = exteriorDoors(w.map).find((d) => d.building === to.building && d.link.interior === to.interior);
      if (!door) return;
      this.setAside(door);
    }
    this.setFloor(to.interior, to.floor, to.building, (m) => (freeAt(m, at.x, at.y) ? at : m.spawn), false);
    this.h.emit({ type: 'enterInterior', interior: to.interior, floor: to.floor, building: to.building });
  }

  // ───────────────────────── moving between scenes ─────────────────────────

  private fade(to: 'enter' | 'exit' | 'floor', go: () => void): void {
    this.pending = { t: DOOR_FADE, go };
    this.h.halt();
    this.h.world.player.path = null;
    this.h.emit({ type: 'doorFade', to, seconds: DOOR_FADE });
  }

  /** Takes a flight of stairs to the floor it leads to (unless that way is still shut). */
  private take(s: Flight): void {
    const where = this.where!;
    const fl = this.floor();
    if (s.dir < 0 && fl?.sealed && this.h.world.progress.quest.index < fl.sealed.quest) {
      this.cool = 2;
      this.h.notice(fl.sealed.text);
      return;
    }
    const to = flightTarget(where.interior, where.floor, s);
    if (!to) return;
    this.fade('floor', () => {
      const from = where.floor;
      this.setFloor(where.interior, to.level, where.building, () => to.flight.landing);
      this.h.emit({ type: 'changeFloor', interior: where.interior, from, to: to.level });
    });
  }

  /** Sets the zone's map and people aside, to be put back on the way out. */
  private setAside(door: ExteriorDoor): void {
    const w = this.h.world;
    const stash: Entity[] = [];
    for (const e of [...w.entities.values()])
      if (!this.carried(e)) {
        stash.push(e);
        w.entities.delete(e.id);
      }
    this.outside = { map: w.map, stash };
    this.where = { interior: door.link.interior, floor: 0, building: door.building };
  }

  private goIn(door: ExteriorDoor): void {
    this.setAside(door);
    const k = door.link.door ?? 0;
    this.setFloor(door.link.interior, 0, door.building, (m) => roomFeatures(m).doors[k]?.landing ?? m.spawn);
    this.h.emit({ type: 'enterInterior', interior: door.link.interior, floor: 0, building: door.building });
  }

  /** Builds a floor: its map, its people and monsters (unless `people` is false: a net replica), and the player (and company) at `at`. */
  private setFloor(id: string, level: number, building: string, at: (m: GameMap) => Point, people = true): void {
    const w = this.h.world;
    const def = INTERIORS[id]!;
    const fl = getFloor(id, level)!;
    for (const e of [...w.entities.values()]) if (!this.carried(e)) w.entities.delete(e.id);
    this.people.clear();
    w.telegraphs.length = 0;
    const m = generateInterior(id, level);
    w.map = m;
    this.where = { interior: id, floor: level, building };
    const f = roomFeatures(m);
    this.bringAlong(at(m));
    const p = w.player;
    // Its people, facing the way in.
    const facePoint = f.doors[0]?.landing ?? f.stairs[0]?.landing ?? m.spawn;
    for (const n of people ? (fl.npcs ?? []) : []) {
      const spot = f.spots[n.spot];
      if (!spot) continue;
      const e = this.h.add({
        kind: 'npc',
        x: spot.x,
        y: spot.y,
        sprite: n.sprite,
        name: n.name,
        title: n.title,
      });
      this.h.face(e, facePoint);
      this.people.set(e.id, { lines: n.lines, shop: !!n.shop, said: 0 });
    }
    // Townsfolk who live here and are at home.
    let r = 0;
    for (const fid of people ? (def.residents ?? []) : []) {
      const folk = w.folk.get(fid);
      const spot = f.residents[r % Math.max(1, f.residents.length)];
      if (!folk?.folk?.indoors || !spot) continue;
      const fd = FOLKS[fid]!;
      const e = this.h.add({
        kind: 'npc',
        x: spot.x + (r >= f.residents.length ? 0.6 : 0),
        y: spot.y,
        sprite: fd.sprite,
        name: fd.name,
        title: fd.title,
        radius: fd.child ? 0.2 : 0.28,
      });
      r++;
      this.h.face(e, p);
      this.people.set(e.id, { lines: [...(fd.lines.night ?? []), ...fd.lines.greet], shop: false, said: 0 });
    }
    // Monsters at their spots.
    let k = 0;
    for (const s of people ? (fl.monsters ?? []) : [])
      for (let n = 0; n < s.n; n++) {
        const spot = f.monsters[k++];
        if (spot) this.h.spawnMonster(s.monster, spot.x, spot.y);
      }
    w.progress.indoors = { interior: id, floor: level, building, x: p.x, y: p.y };
    this.cool = 0.8;
  }

  /** Out by the door to the zone outside: its map and people back, the player at the door. */
  private leave(): void {
    const w = this.h.world;
    const out = this.outside;
    const where = this.where;
    if (!out || !where) return;
    for (const e of [...w.entities.values()]) if (!this.carried(e)) w.entities.delete(e.id);
    this.people.clear();
    w.telegraphs.length = 0;
    w.map = out.map;
    for (const e of out.stash) w.entities.set(e.id, e);
    this.outside = null;
    this.where = null;
    w.progress.indoors = null;
    const door = exteriorDoors(w.map).find((d) => d.building === where.building);
    const spot = door ? nearOpen(w.map, door.out) : w.map.spawn;
    this.bringAlong(spot);
    if (door) {
      // Facing out, away from the door (so standing there does not walk straight back in).
      const [fx, fy] = STEP[door.facing];
      this.h.face(w.player, { x: spot.x + fx, y: spot.y + fy });
      w.player.dirX = fx;
      w.player.dirY = fy;
    }
    this.cool = 0.8;
    this.h.emit({ type: 'exitInterior', interior: where.interior, building: where.building });
  }

  /** The player to `at`, and the pet and party close by. */
  private bringAlong(at: Point): void {
    const w = this.h.world;
    const p = w.player;
    Object.assign(p, { x: at.x, y: at.y, z: 0, vz: 0, jumps: 0, path: null, targetId: null });
    this.h.dismount();
    let k = 0;
    for (const e of w.entities.values()) {
      if (e === p || !this.carried(e)) continue;
      const a = (k++ / 6) * Math.PI * 2 + 0.6;
      const near = nearOpen(w.map, { x: at.x + Math.cos(a) * 0.7, y: at.y + Math.sin(a) * 0.7 });
      Object.assign(e, { x: near.x, y: near.y, path: null, targetId: null });
    }
  }
}
